import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Select, message, Space, Alert, Spin, Button, Divider, Tag, Typography, Badge } from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  TrophyOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { Tournament, TournamentCategory } from '@/types/api.types';
import type { Participant } from '@/types/api.types';
import { useAuth } from '@/contexts/AuthContext';
import UserService from '@/services/user.service';
import TournamentService from '@/services/tournament.service';
import { logger } from '@/utils/logger';
import { Link } from 'react-router-dom';
import {
  StyledModal,
  RegisteredParticipantBadge,
  SelectedParticipantChip,
  StatsContainer,
  StyledCollapse
} from './styles/RegisterParticipantsModal.styles';

const { Option } = Select;
const { Text } = Typography;

interface RegisterParticipantsModalProps {
  visible: boolean;
  tournament: Tournament;
  onCancel: () => void;
  onSuccess: () => void;
}

interface SelectedParticipants {
  [categoryId: number]: number[];
}

export const RegisterParticipantsModal: React.FC<RegisterParticipantsModalProps> = ({
  visible,
  tournament,
  onCancel,
  onSuccess,
}) => {
  // ВСЕ ХУКИ ДОЛЖНЫ БЫТЬ ВЫЗВАНЫ ДО ЛЮБЫХ УСЛОВНЫХ RETURN
  const { user } = useAuth();

  const handleCancel = () => {
    onCancel();
    // Родитель при необходимости обновит контент через свой callback
  };

  const handleSuccess = () => {
    onSuccess();
    // Родитель обновит контент через свой callback, остаёмся на странице
  };
  const [selectedParticipants, setSelectedParticipants] = useState<SelectedParticipants>({});
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [userClub, setUserClub] = useState<{ id: number; name: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [tournamentCategories, setTournamentCategories] = useState<TournamentCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [registeredParticipants, setRegisteredParticipants] = useState<Record<string, number>>({});  // Map of participantId -> categoryId

  // Состояние для участников категории
  const [categoryParticipantsMap, setCategoryParticipantsMap] = useState<Record<number, any[]>>({});
  const [loadingCategoryParticipantsMap, setLoadingCategoryParticipantsMap] = useState<Record<number, boolean>>({});

  // Состояние для сворачивания категорий (по умолчанию все свернуты)
  const [collapsedCategories, setCollapsedCategories] = useState<string[]>([]);

  // Состояние для отслеживания участников, которые были сняты с регистрации
  const [unregisteringParticipants, setUnregisteringParticipants] = useState<Set<string>>(new Set());

  // Теперь выполняем проверки ПОСЛЕ всех хуков
  if (!tournament) {
    return null;
  }

  if (!onCancel || !onSuccess) {
    return null;
  }

  // Проверяем, что пользователь загружен
  if (!user) {
    return null;
  }

  // Мемоизированная функция для получения зарегистрированных участников категории
  const getRegisteredParticipantsForCategory = useCallback((categoryId: number) => {
    const categoryParticipants = categoryParticipantsMap[categoryId] || [];
    const registeredInCategory = categoryParticipants.filter(p => {
      const participantId = p.participantId || p.userId || (p.participant ? p.participant.id : null) || p.id;
      const isRegistered = participantId && registeredParticipants[String(participantId)] === categoryId;
      return isRegistered;
    });
    return registeredInCategory;
  }, [categoryParticipantsMap, registeredParticipants]);

  // Мемоизированная функция для подсчета участников в категории
  const getCategoryParticipantCount = useCallback((categoryId: number) => {
    const registeredInCategory = getRegisteredParticipantsForCategory(categoryId);
    const selectedCount = selectedParticipants[categoryId]?.length || 0;
    const registeredCount = registeredInCategory.length;
    const category = tournamentCategories.find(c => c.id === categoryId);
    const maxParticipants = category?.maxParticipants;

    // Получаем количество доступных для регистрации участников из клуба пользователя
    const eligibleParticipants = participants.filter(participant => {
      if (!participant || !participant.id) return false;

      // Фильтруем только участников из клуба пользователя
      if (!userClub?.id) {
        return false;
      }
      
      // Извлекаем clubId участника из разных возможных источников
      const participantClubId = participant.clubId || 
                                (participant.club && participant.club.id) ||
                                (participant.clubParticipants && participant.clubParticipants[0]?.clubId) ||
                                (participant.clubParticipants && participant.clubParticipants.some((cp: any) => cp && cp.clubId) && 
                                 participant.clubParticipants.find((cp: any) => cp && cp.clubId)?.clubId);
      
      // Строгая проверка: участник должен быть из клуба пользователя
      if (!participantClubId || participantClubId !== userClub.id) {
        return false;
      }

      // Находим категорию турнира (tournament_categories.id = categoryId, category.categoryId = categories.id)
      const category = tournamentCategories.find(c => c.id === categoryId);
      if (!category) return false;

      // Показываем только тех, у кого в клубе указана эта категория (categories.id)
      const categoryIdValue = category.categoryId ?? category.id;
      const participantCategoryId = participant.categoryId ?? participant.category?.id;
      const isEligibleByCategory = participantCategoryId != null && Number(participantCategoryId) === Number(categoryIdValue);

      // Проверяем, не зарегистрирован ли уже участник в другой категории
      const isAlreadyRegistered = registeredParticipants[String(participant.id)] &&
                                 registeredParticipants[String(participant.id)] !== categoryId;
      if (isAlreadyRegistered) return false;

      // Проверяем, не выбран ли уже участник в другой категории
      const isSelectedInOtherCategory = Object.entries(selectedParticipants)
        .some(([catId, participantIds]) =>
          Number(catId) !== categoryId && participantIds.includes(participant.id)
        );
      if (isSelectedInOtherCategory) return false;

      // Проверяем, зарегистрирован ли участник уже в этой категории
      const isRegisteredInThisCategory = registeredParticipants[String(participant.id)] === categoryId;
      
      // Участник подходит, если он либо соответствует категории, либо уже зарегистрирован в ней
      return isEligibleByCategory || isRegisteredInThisCategory;
    });

    // Определяем статус регистрации
    const hasLimit = maxParticipants && maxParticipants > 0;
    const totalAfterRegistration = registeredCount + selectedCount;
    const hasEligibleParticipants = eligibleParticipants.length > 0;
    const hasSpaceInCategory = !hasLimit || totalAfterRegistration < maxParticipants;
    const canRegister = hasEligibleParticipants && hasSpaceInCategory;
    const isFull = hasLimit && totalAfterRegistration >= maxParticipants;
    const availableSlots = hasLimit ? maxParticipants - registeredCount : null;

    return {
      registered: registeredCount,
      selected: selectedCount,
      total: totalAfterRegistration,
      max: maxParticipants,
      hasLimit,
      canRegister,
      isFull,
      availableSlots,
      maxDisplay: hasLimit ? maxParticipants.toString() : 'без лимита',
      eligibleCount: eligibleParticipants.length,
      hasEligibleParticipants
    };
  }, [participants, userClub, tournamentCategories, registeredParticipants, selectedParticipants, getRegisteredParticipantsForCategory]);

  // Мемоизированная функция для фильтрации категорий
  const availableCategories = useMemo(() => {
    return tournamentCategories.filter(category => {
      const participantCount = getCategoryParticipantCount(category.id);
      // Показываем категории где есть участники для регистрации или уже есть зарегистрированные/выбранные
      return participantCount.hasEligibleParticipants ||
             participantCount.registered > 0 ||
             participantCount.selected > 0;
    });
  }, [tournamentCategories, getCategoryParticipantCount]);


  // Функция для загрузки зарегистрированных участников
  const fetchRegisteredParticipants = async () => {
    if (!visible || !tournament || !tournament.id) return;

    try {
      // Сбрасываем старые данные перед загрузкой новых
      setRegisteredParticipants({});

      // Используем метод из TournamentService для получения зарегистрированных участников
      // Этот метод содержит несколько резервных способов получения данных
      const registeredData = await TournamentService.getRegisteredParticipants(tournament.id);

      // Create a map of participantId -> categoryId
      const registeredMap: Record<string, number> = {};

      try {
        if (Array.isArray(registeredData) && registeredData.length > 0) {
          registeredData.forEach((item: any) => {
            // Пытаемся извлечь participantId из разных возможных полей
            const participantId = item?.participantId || item?.userId || item?.participant?.id || item?.id;
            const categoryId = item?.categoryId || item?.category_id || item?.category?.id;

            if (participantId && categoryId) {
              registeredMap[String(participantId)] = Number(categoryId);
            }
          });
        }
      } catch (error) {
        // Ошибка обработки данных зарегистрированных участников
      }

      // Выводим в консоль информацию о том, кто из клуба зарегистрирован
      if (userClub && userClub.id && participants.length > 0) {
        const registeredClubParticipants = participants.filter(p => {
          const isRegistered = registeredMap[p.id] || registeredMap[String(p.id)];
          return isRegistered;
        });

        if (registeredClubParticipants.length > 0) {
          // Группируем по категориям
          const participantsByCategory: Record<number, any[]> = {};
          registeredClubParticipants.forEach(p => {
            const categoryId = registeredMap[p.id] || registeredMap[String(p.id)];
            if (categoryId) {
              if (!participantsByCategory[categoryId]) {
                participantsByCategory[categoryId] = [];
              }
              participantsByCategory[categoryId].push(p);
            }
          });

          // Выводим информацию по категориям
          if (tournament.categories) {
            // Для каждой категории получаем участников
            for (const category of tournament.categories) {
              try {
                // Используем новый метод для получения участников категории
                const categoryParticipants = await TournamentService.getCategoryParticipants(
                  tournament.id,
                  category.id
                );

                // Фильтруем участников клуба в категории (переменная используется для логики, но не отображается)
                categoryParticipants.filter((cp: any) => {
                  const participant = cp.participant || cp;
                  return participant && participant.clubId === userClub.id;
                });

              } catch (error) {

                // Используем старый метод как запасной вариант (переменная используется для логики, но не отображается)
                registeredClubParticipants.filter(p => {
                  const regCatId = registeredMap[p.id] || registeredMap[String(p.id)];
                  return regCatId === category.id;
                });

              }
            }
          }
        }
      }

      // Устанавливаем карту зарегистрированных участников
      setRegisteredParticipants(registeredMap);

      // Обновляем выбранных участников на основе уже зарегистрированных
      if (Object.keys(registeredMap).length > 0) {
        const newSelectedParticipants: SelectedParticipants = {};

        Object.entries(registeredMap).forEach(([participantId, categoryId]) => {
          const pId = Number(participantId);
          const cId = Number(categoryId);

          if (!newSelectedParticipants[cId]) {
            newSelectedParticipants[cId] = [];
          }

          // Проверяем, не добавлен ли уже этот участник
          if (!newSelectedParticipants[cId].includes(pId)) {
            newSelectedParticipants[cId].push(pId);
          }
        });

        setSelectedParticipants(newSelectedParticipants);
      }
    } catch (error) {
      logger.error('Error fetching registered participants:', error);
      // Показываем сообщение об ошибке пользователю
      message.error('Не удалось загрузить зарегистрированных участников');
    }
  };

  // Fetch registered participants when the modal opens
  useEffect(() => {


    fetchRegisteredParticipants();
  }, [visible, tournament, participants, userClub]);

  // Fetch tournament categories if they're not already included in the tournament object
  useEffect(() => {
    const fetchTournamentCategories = async () => {
      if (!visible || !tournament || !tournament.id) return;

      try {
        // Сбрасываем состояния при открытии модала
        setTournamentCategories([]);
        setCategoryParticipantsMap({});
        setLoadingCategoryParticipantsMap({});
        setSelectedParticipants({});
        setRegisteredParticipants({}); // Также сбрасываем зарегистрированных участников

        // ВАЖНО: Всегда загружаем категории через API, чтобы получить правильные ID из tournament_categories
        // НЕ используем tournament.categories напрямую, т.к. там могут быть ID из таблицы categories
        // вместо tournament_categories

      try {
        setLoadingCategories(true);

        // Use the TournamentService to fetch categories
        const categoriesData = await TournamentService.getTournamentCategories(tournament.id);

        if (Array.isArray(categoriesData)) {
          setTournamentCategories(categoriesData);
        } else {
          logger.error('Invalid categories data format:', categoriesData);
          setTournamentCategories([]);
        }
      } catch (error) {
        logger.error('Error fetching tournament categories:', error);
        // Don't show error message to user, just log it
        setTournamentCategories([]);
      } finally {
        setLoadingCategories(false);
      }
      } catch (outerError) {
        logger.error('Unexpected error in fetchTournamentCategories:', outerError);
        setTournamentCategories([]);
        setLoadingCategories(false);
      }
    };

    fetchTournamentCategories();
  }, [visible, tournament]);

  // Fetch category participants for each category
  useEffect(() => {
    const fetchCategoryParticipants = async (categoryId: number) => {
      if (!visible || !tournament || !tournament.id) return;

      // Проверяем, не загружаем ли мы уже эту категорию
      if (loadingCategoryParticipantsMap[categoryId]) return;

      try {
        // Mark this category as loading
        setLoadingCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: true }));

        // Use the new method to get category participants
        const participants = await TournamentService.getCategoryParticipants(
          tournament.id,
          categoryId
        );

        // Store the participants in the map
        setCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: participants }));
      } catch (error) {

        // Check if the error is a 404 (category not found) or 500 (internal server error)
        if ((error as any)?.response?.status === 404 || (error as any)?.response?.status === 500) {
          // Set empty array for this category
          setCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: [] }));
          setLoadingCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: false }));
          return;
        }

        // If the new method failed, use the old method as fallback
        const registeredIds = new Set<number>();

        // Find all participants registered in this category
        Object.entries(registeredParticipants).forEach(([participantId, catId]) => {
          if (Number(catId) === categoryId) {
            registeredIds.add(Number(participantId));
          }
        });

        // Get detailed information for each participant
        const fallbackParticipants = Array.from(registeredIds).map(id => {
          const participant = participants.find(p =>
            p.id === id || Number(p.id) === id || String(p.id) === String(id)
          );

          return {
            id,
            participantId: id,
            categoryId: categoryId,
            participant: participant ? {
              id: participant.id,
              firstName: participant.firstName,
              lastName: participant.lastName,
              middleName: participant.middleName
            } : undefined
          };
        });

        // Store the fallback participants
        setCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: fallbackParticipants }));
      } finally {
        setLoadingCategoryParticipantsMap(prev => ({ ...prev, [categoryId]: false }));
      }
    };

    // Fetch participants for each category
    if (tournamentCategories.length > 0 && visible && tournament) {
      tournamentCategories.forEach(category => {
        fetchCategoryParticipants(category.id);
      });

      // Также принудительно загружаем зарегистрированных участников
      fetchRegisteredParticipants();
    }
  }, [visible, tournament, tournamentCategories]);

  // Fetch club data and participants
  useEffect(() => {
    const fetchClubData = async () => {
      // Fetch club data and participants

      if (!visible || !user) {
        return;
      }

      // Check if user has appropriate role (manager)
      const allowedRoles = ['manager'];
      if (!allowedRoles.includes(user.role)) {
        return;
      }

      // User has appropriate role

      try {
        setLoading(true);

        // Get the manager ID
        const managerId = user.id;

        // Check if user already has clubId
        let clubData;
        if (user.clubId) {
          try {
            const directClubResponse = await UserService.getClubById(user.clubId);
            if (directClubResponse && directClubResponse.data) {
              clubData = directClubResponse.data;
              setUserClub(clubData);
            }
          } catch (directClubError) {
            logger.error('Error fetching club directly:', directClubError);
          }
        }

        // If we don't have club data yet, try fetching via manager endpoint
        if (!clubData) {
          try {
            const clubResponse = await UserService.getManagerClub(managerId);
            if (!clubResponse || !clubResponse.data) {
              logger.error('No club data returned for manager:', managerId);
              message.error('Не удалось получить информацию о клубе');
              setLoading(false);
              return;
            }

            clubData = clubResponse.data;
            setUserClub(clubData);
          } catch (clubError) {
            logger.error('Error fetching club via manager endpoint:', clubError);
            if (!clubData) {
              logger.error('Failed to get club data by any method');
              message.error('Не удалось получить информацию о клубе');
              setLoading(false);
              return;
            }
          }
        }

        // Now fetch participants if we have club data
        if (!clubData || !clubData.id) {
          setLoading(false);
          return;
        }

        let participantsResponse;
        try {
          participantsResponse = await UserService.getClubParticipants(clubData.id);
        } catch (participantsError) {
          logger.error('Error fetching participants:', participantsError);
          message.error('Не удалось загрузить участников клуба');
          setLoading(false);
          return;
        }

        // Extract participants data from the response
        let participantsData: any[] = [];

        if (!participantsResponse || !participantsResponse.data) {
          setParticipants([]);
          return;
        }

        // Try different response formats

        if (Array.isArray(participantsResponse.data)) {
          // Format {data: Array}
          participantsData = participantsResponse.data;
        } else if (participantsResponse.data && participantsResponse.data.data && Array.isArray(participantsResponse.data.data)) {
          // Format {data: {data: Array}}
          participantsData = participantsResponse.data.data;
        } else if (participantsResponse.data && participantsResponse.data.success && participantsResponse.data.data && Array.isArray(participantsResponse.data.data)) {
          // Format {data: {success: true, data: Array}}
          participantsData = participantsResponse.data.data;
        } else {
          // Try to find any array in the response
          const findArrayInObject = (obj: any): any[] | null => {
            for (const key in obj) {
              if (Array.isArray(obj[key])) {
                return obj[key];
              } else if (typeof obj[key] === 'object' && obj[key] !== null) {
                const result = findArrayInObject(obj[key]);
                if (result) return result;
              }
            }
            return null;
          };

          const foundArray: any[] | null = findArrayInObject(participantsResponse);
          if (foundArray) {
            participantsData = foundArray;
          } else {
            setParticipants([]);
            return;
          }
        }
        try {
          // Convert the raw data to Participant objects
          const convertedParticipants = participantsData
            .filter((p: any) => {
              // Filter out any null or undefined items
              if (!p) {
                return false;
              }
              
              return true;
            })
            .map((p: any): Participant | null => {
              try {
                // Check if this is a ClubParticipant with a participant property
                if (p.participant) {
                  const raw = p.participant;
                  // Извлекаем clubId из ClubParticipant или из participant
                  const participantClubId = p.clubId || p.club_id || (p.club && p.club.id) ||
                                           (raw.clubId || raw.club_id || (raw.club && raw.club.id));

                  // Фильтруем только участников из клуба пользователя
                  if (clubData && clubData.id && participantClubId && participantClubId !== clubData.id) {
                    return null;
                  }

                  // Нормализуем categoryId из вложенного category (API может вернуть category.id вместо categoryId)
                  const categoryId = raw.categoryId ?? raw.category_id ?? (raw.category && (raw.category.id ?? (raw as any).category?.id)) ?? undefined;
                  return { ...raw, clubId: participantClubId ?? raw.clubId, categoryId, category: raw.category ?? (categoryId ? { id: categoryId, name: '' } : undefined) } as Participant;
                }

                // Otherwise, assume this is a direct participant object
                // Check if it has the minimum required properties
                if (!p.id) {
                  return null;
                }

                // Извлекаем clubId участника
                const participantClubId = p.clubId || p.club_id || (p.club && p.club.id) || 
                                         (p.clubParticipants && p.clubParticipants[0]?.clubId) || 0;

                // Фильтруем только участников из клуба пользователя
                if (clubData && clubData.id && participantClubId && participantClubId !== clubData.id) {
                  return null;
                }

                // Create participant object; categoryId — из полей или из вложенного category (подгрузка категорий)
                const categoryId = p.categoryId ?? p.category_id ?? (p.category && (p.category.id ?? (p as any).category?.id));
                const participantData = {
                  id: p.id || p.participantId || p.userId,
                  firstName: p.firstName || p.first_name || '',
                  lastName: p.lastName || p.last_name || '',
                  middleName: p.middleName || p.middle_name || '',
                  dateOfBirth: p.dateOfBirth || p.date_of_birth || '',
                  gender: (p.gender as 'male' | 'female') || 'male',
                  weight: typeof p.weight === 'number' ? p.weight : (typeof p.weight === 'string' ? parseFloat(p.weight) || 0 : 0),
                  beltLevel: p.beltLevel || p.belt_level || '',
                  categoryId: categoryId ?? undefined,
                  clubId: participantClubId,
                  coachId: p.coachId || p.coach_id || null,
                  status: (p.status as 'active' | 'inactive') || 'active',
                  createdAt: p.createdAt || p.created_at || '',
                  updatedAt: p.updatedAt || p.updated_at || '',
                  club: p.club,
                  clubParticipants: p.clubParticipants,
                  ...(p.category && { category: p.category })
                } as Participant;
                
                return participantData;
              } catch (error) {
                logger.error('Error converting participant:', error, p);
                return null;
              }
            })
            .filter((p): p is Participant => p !== null) // Filter out any null results
            .filter((p: Participant) => {
              // Дополнительная фильтрация: убеждаемся, что clubId соответствует клубу пользователя
              if (!clubData || !clubData.id) {
                return false;
              }
              
              const participantClubId = p.clubId || 
                                       (p.club && p.club.id) || 
                                       (p.clubParticipants && p.clubParticipants[0]?.clubId);
              
              return participantClubId === clubData.id;
            });

          setParticipants(convertedParticipants);
        } catch (conversionError) {
          logger.error('Error converting participants data:', conversionError);
          setParticipants([]);
        }
      } catch (error) {
        logger.error('Error fetching club data:', error);
        message.error('Ошибка при загрузке данных клуба');
      } finally {
        setLoading(false);
      }
    };

    fetchClubData();
  }, [visible, user]);

  const handleParticipantSelect = (categoryId: number, participantIds: number[]) => {
    // Ограничиваем выбор по лимиту мест в категории
    let idsToSet = participantIds || [];
    if (idsToSet.length > 0) {
      const participantCount = getCategoryParticipantCount(categoryId);
      if (participantCount.hasLimit && participantCount.availableSlots != null && participantCount.availableSlots >= 0) {
        if (idsToSet.length > participantCount.availableSlots) {
          message.warning(`В этой категории доступно только ${participantCount.availableSlots} мест. Выбор ограничен.`);
          idsToSet = idsToSet.slice(0, participantCount.availableSlots);
        }
      }
    }

    setSelectedParticipants(prev => {
      if (!idsToSet || idsToSet.length === 0) {
        // If no participants selected, remove the category
        const newState = { ...prev };
        delete newState[categoryId];
        return newState;
      }

      // Check if any of the selected participants are already selected in other categories
      const allSelectedParticipants = Object.entries(prev)
        .filter(([catId]) => Number(catId) !== categoryId) // Exclude current category
        .flatMap(([_, ids]) => ids); // Get all participant IDs from other categories

      const alreadySelectedParticipants = idsToSet.filter(id =>
        allSelectedParticipants.includes(id)
      );

      if (alreadySelectedParticipants.length > 0) {
        // Find participant names for better error message
        const participantNames = alreadySelectedParticipants.map(id => {
          const participant = participants.find(p => p.id === id);
          return participant ? `${participant.firstName} ${participant.lastName}` : `ID: ${id}`;
        }).join(', ');

        message.warning(`Участники ${participantNames} уже зарегистрированы в других категориях`);

        // Filter out already selected participants
        const availableParticipants = idsToSet.filter(id =>
          !allSelectedParticipants.includes(id)
        );

        return {
          ...prev,
          [categoryId]: availableParticipants
        };
      }

      return {
        ...prev,
        [categoryId]: idsToSet
      };
    });
  };

  const handleUnregisterParticipant = async (participantId: number, categoryId: number) => {
    const key = `${participantId}-${categoryId}`;
    if (unregisteringParticipants.has(key)) {
      return; // Already processing
    }

    try {
      setUnregisteringParticipants(prev => new Set(prev).add(key));
      
      await TournamentService.unregisterParticipant(tournament.id, participantId);
      
      message.success('Участник успешно снят с регистрации');
      
      // Обновляем список зарегистрированных участников
      const newRegistered = { ...registeredParticipants };
      delete newRegistered[String(participantId)];
      setRegisteredParticipants(newRegistered);
      
      // Обновляем данные категории
      const updatedCategoryParticipants = { ...categoryParticipantsMap };
      if (updatedCategoryParticipants[categoryId]) {
        updatedCategoryParticipants[categoryId] = updatedCategoryParticipants[categoryId].filter(
          (p: any) => (p.participantId || p.userId || p.id) !== participantId
        );
        setCategoryParticipantsMap(updatedCategoryParticipants);
      }
      
      // Вызываем handleSuccess для обновления данных в родительском компоненте и обновления страницы
      handleSuccess();
    } catch (error: any) {
      logger.error('Error unregistering participant:', error);
      message.error(error?.response?.data?.message || 'Не удалось снять участника с регистрации');
    } finally {
      setUnregisteringParticipants(prev => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    }
  };

  const handleUnregisterClub = async () => {
    if (!userClub?.id) {
      message.error('Не удалось определить клуб');
      return;
    }

    try {
      setLoading(true);
      
      const confirmed = window.confirm(
        `Вы уверены, что хотите снять всю команду с турнира?\n\nЭто действие удалит всех участников клуба «${userClub.name}» с данного турнира и удалит регистрацию клуба.\n\nЕсли сетки уже сформированы, их потребуется переформировать в затронутых категориях на странице жеребьёвки.`
      );

      if (!confirmed) {
        setLoading(false);
        return;
      }

      const result = await TournamentService.unregisterClub(tournament.id, userClub.id, { reformBrackets: false });

      if (result.categoriesNeedingReform > 0) {
        message.success(
          `Команда успешно снята с турнира. В ${result.categoriesNeedingReform} категории(ях) нужно переформировать сетки на странице жеребьёвки.`
        );
      } else {
        message.success('Команда успешно снята с турнира');
      }
      
      // Обновляем список зарегистрированных участников
      setRegisteredParticipants({});
      
      // Вызываем handleSuccess для обновления данных в родительском компоненте и обновления страницы
      // handleSuccess уже обновит страницу, поэтому не нужно вызывать handleCancel
      handleSuccess();
    } catch (error: any) {
      logger.error('Error unregistering club:', error);
      message.error(error?.response?.data?.message || 'Не удалось снять команду с турнира');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Create registrations for all selected participants across all categories
      interface Registration {
        categoryId: number;
        participantId: number;
        tournamentId: number;
        clubId?: number;
      }

      const registrations: (Registration | null)[] = Object.entries(selectedParticipants)
        .filter(([_, participantIds]) => participantIds && participantIds.length > 0)
        .flatMap(([categoryId, participantIds]) =>
          participantIds.map((participantId: number) => {
            // Проверяем, не зарегистрирован ли участник уже в этой категории
            if (Object.entries(registeredParticipants).some(([pid, cid]) =>
                (Number(pid) === participantId || String(pid) === String(participantId)) &&
                Number(cid) === Number(categoryId)
              )) {
              return null; // Пропускаем уже зарегистрированных участников
            }

            // Находим категорию, чтобы получить правильный categoryId (categories.id)
            const category = tournamentCategories.find(c => c.id === Number(categoryId));
            if (!category) {
              logger.error(`Category with id ${categoryId} not found in tournamentCategories`);
              return null; // Пропускаем регистрацию, если категория не найдена
            }
            
            // Используем categoryId (categories.id), если он есть, иначе используем id (tournament_categories.id) как fallback
            // ВАЖНО: categoryId должен быть categories.id, а не tournament_categories.id
            const actualCategoryId = category.categoryId || category.id;
            
            if (!category.categoryId) {
              logger.warn(`Category ${category.id} (${category.name}) не имеет categoryId (categories.id), используется id как fallback`);
            }
            
            // Ensure all values are properly typed
            const registration = {
              categoryId: Number(actualCategoryId), // Используем categories.id, а не tournament_categories.id
              participantId: Number(participantId),
              tournamentId: Number(tournament.id),
              clubId: userClub?.id ? Number(userClub.id) : undefined
            };

            return registration;
          })
        )
        .filter(registration => registration !== null); // Фильтруем null-значения

      // Подсчитаем, сколько участников было пропущено из-за того, что они уже зарегистрированы
      const totalSelected = Object.values(selectedParticipants).flat().length;
      const skippedCount = totalSelected - registrations.length;

      if (registrations.length === 0) {
        if (skippedCount > 0) {
          message.info(`Все выбранные участники (${skippedCount}) уже зарегистрированы в соответствующих категориях`);
        } else {
          message.warning('Выберите хотя бы одного участника для регистрации');
        }
        setLoading(false);
        return;
      }
      
      // Если есть пропущенные участники, но есть и новые для регистрации
      if (skippedCount > 0) {
        message.info(`${skippedCount} участник(ов) уже зарегистрированы и будут пропущены`);
      }

      // Call the API to register participants
      // Add a delay to ensure the transaction completes
      const result = await TournamentService.registerParticipants(tournament.id, registrations);

      if (result?.success) {
        // Show success message with details
        const successCount = result.data?.successful?.length || 0;
        const failedCount = result.data?.failed?.length || 0;

        if (failedCount > 0) {
          message.warning(`Зарегистрировано ${successCount} участников, не удалось зарегистрировать ${failedCount} участников`);

          // Log failed registrations
          if (result.data?.failed) {
            result.data.failed.forEach((failure: any) => {
              logger.warn(`Failed to register participant ${failure?.participantId}: ${failure?.error}`);
            });
          }
        } else {
          message.success(`Успешно зарегистрировано ${successCount} участников`);
        }

        // Reset selected participants
        setSelectedParticipants({});

        // Add a small delay to ensure the database has time to update
        await new Promise(resolve => setTimeout(resolve, 1000));

        // Refresh the list of registered participants
        try {
          await TournamentService.getRegisteredParticipants(tournament.id);
        } catch (error) {
          // Silent fail - we don't want to block the user if verification fails
        }

        handleSuccess();
      } else {
        // Show error message
        message.error('Ошибка при регистрации участников');
      }
    } catch (error: any) {
      logger.error('Error registering participants:', error);

      // Show a more detailed error message if available
      if (error.response?.data?.message) {
        message.error(`Ошибка: ${error.response.data.message}`);
      } else {
        message.error('Ошибка при регистрации участников');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryCard = useCallback((category: TournamentCategory) => {
    // Проверки безопасности
    if (!category) {
      return null;
    }

    if (!category.id) {
      return null;
    }

    try {
      // Removed verbose category logging

    // Примечание: параметры weightFrom и weightTo больше не используются, так как фильтрация происходит по ID категории

    
    // Счетчики для отладки
    let filteredByClub = 0;
    let filteredByOtherCategory = 0;

    // Фильтруем участников по ID категории и клубу пользователя
    const eligibleParticipants = participants.filter(participant => {
      // Проверяем, что участник существует
      if (!participant) {
        return false;
      }

      // Фильтруем только участников из клуба пользователя
      if (userClub?.id) {
        // Извлекаем clubId участника из разных возможных источников
        const participantClubId = participant.clubId || 
                                 (participant.club && participant.club.id) ||
                                 (participant.clubParticipants && participant.clubParticipants[0]?.clubId) ||
                                 (participant.clubParticipants && participant.clubParticipants.some((cp: any) => cp && cp.clubId) && 
                                  participant.clubParticipants.find((cp: any) => cp && cp.clubId)?.clubId);
        
        // Строгая проверка: участник должен быть из клуба пользователя
        if (!participantClubId || participantClubId !== userClub.id) {
          filteredByClub++;
          return false;
        }
      } else {
        // Если клуб пользователя не определен, не показываем участников
        filteredByClub++;
        return false;
      }

      // Проверяем, зарегистрирован ли участник уже в ДРУГОЙ категории этого турнира
      const isRegisteredInAnotherCategory = participant.id && 
        registeredParticipants[String(participant.id)] && 
        registeredParticipants[String(participant.id)] !== category.id;
      
      // Если участник уже зарегистрирован в другой категории, не показываем его
      if (isRegisteredInAnotherCategory) {
        filteredByOtherCategory++;
        return false;
      }

      // Проверяем, не выбран ли уже участник в другой категории (как в getCategoryParticipantCount)
      const isSelectedInOtherCategory = participant.id && Object.entries(selectedParticipants)
        .some(([catId, participantIds]) =>
          Number(catId) !== category.id && participantIds.includes(participant.id)
        );
      if (isSelectedInOtherCategory) {
        filteredByOtherCategory++;
        return false;
      }

      // Показываем только тех, у кого в клубе указана эта категория (categories.id)
      const categoryIdValue = category.categoryId ?? category.id;
      const participantCategoryId = participant.categoryId ?? participant.category?.id;
      const isEligibleByCategory = participantCategoryId != null && Number(participantCategoryId) === Number(categoryIdValue);

      // Проверяем, зарегистрирован ли участник в ЭТОЙ категории
      const isRegisteredInThisCategory = participant.id ? registeredParticipants[String(participant.id)] === category.id : false;

      // Участник подходит, если он либо соответствует категории, либо уже зарегистрирован в ней
      const isEligible = isEligibleByCategory || isRegisteredInThisCategory;


      return isEligible;
    });


    // Sort eligible participants by weight to make selection easier
    const sortedParticipants = [...eligibleParticipants].sort((a, b) => a.weight - b.weight);

    // Получаем информацию о количестве участников
    const participantCount = getCategoryParticipantCount(category.id);

    // Создаем заголовок с информацией о участниках
    const categoryTitle = (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <TrophyOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
          <Text strong style={{ fontSize: '15px' }}>{category.name || `Категория ${category.id}`}</Text>
          {/* Индикатор возможности регистрации */}
          {participantCount.isFull ? (
            <Tag icon={<CloseCircleOutlined />} color="error" style={{ margin: 0, borderRadius: '12px', padding: '2px 10px' }}>
              ЗАПОЛНЕНО
            </Tag>
          ) : participantCount.canRegister ? (
            <Tag icon={<CheckCircleOutlined />} color="success" style={{ margin: 0, borderRadius: '12px', padding: '2px 10px' }}>
              МОЖНО РЕГИСТРИРОВАТЬ
            </Tag>
          ) : null}
        </div>
        <StatsContainer>
          {participantCount.selected > 0 && (
            <Badge count={participantCount.selected} showZero style={{ backgroundColor: '#1890ff' }}>
              <Tag color="processing" style={{ margin: 0, borderRadius: '12px', padding: '4px 10px' }}>
                <UserOutlined /> Выбрано
              </Tag>
            </Badge>
          )}
          <Tag 
            color={participantCount.registered > 0 ? 'success' : 'default'} 
            style={{ margin: 0, borderRadius: '12px', padding: '4px 10px' }}
          >
            <TeamOutlined /> {participantCount.registered}/{participantCount.maxDisplay}
          </Tag>
          {participantCount.hasLimit && participantCount.availableSlots !== null && (
            <Tag 
              color={participantCount.availableSlots > 0 ? 'success' : 'error'} 
              style={{ margin: 0, borderRadius: '12px', padding: '4px 10px' }}
            >
              Свободно: {participantCount.availableSlots}
            </Tag>
          )}
          {participantCount.hasEligibleParticipants && (
            <Tag color="blue" style={{ margin: 0, borderRadius: '12px', padding: '4px 10px' }}>
              Доступно: {participantCount.eligibleCount}
            </Tag>
          )}
        </StatsContainer>
      </div>
    );

    return {
      key: category.id.toString(),
      label: categoryTitle,
      style: { marginBottom: '8px' },
      children: (
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <strong>Спортсмены в этой категории:</strong> <span style={{ color: '#52c41a' }}>{eligibleParticipants.length}</span> из {participants.length}
            {selectedParticipants[category.id] && selectedParticipants[category.id].length > 0 && (
              <span style={{ marginLeft: '10px', color: '#1890ff' }}>
                (Выбрано: <strong>{selectedParticipants[category.id].length}</strong>)
              </span>
            )}
          </div>

          {/* Показываем список уже зарегистрированных участников */}
          <div style={{ marginTop: '16px', marginBottom: '20px' }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                Уже зарегистрированные участники:
              </Text>
            </div>

            {/* Получаем список зарегистрированных участников в этой категории */}
            {loadingCategoryParticipantsMap[category.id] ? (
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <Spin size="small" /> Загрузка участников...
              </div>
            ) : (() => {
              const registeredInCategory = getRegisteredParticipantsForCategory(category.id);
              return registeredInCategory.length === 0 ? (
                <div style={{ 
                  color: '#999', 
                  padding: '16px', 
                  textAlign: 'center', 
                  marginTop: '8px', 
                  border: '1px dashed #d9d9d9', 
                  borderRadius: '8px',
                  background: '#fafafa'
                }}>
                  <Text type="secondary">Нет зарегистрированных участников в этой категории</Text>
                </div>
              ) : (
                // Отображаем список зарегистрированных участников
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                  {registeredInCategory.map((p) => {
                  if (!p) return null; // Skip null or undefined participants
                  const participant = p.participant || p;
                  const participantId = p.participantId || p.userId || (participant ? participant.id : null) || p.id;

                  if (participant) {
                    // Если участник найден в списке
                    const isUnregistering = unregisteringParticipants.has(`${participantId}-${category.id}`);
                    return (
                      <RegisteredParticipantBadge key={participantId}>
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                        <span>
                          {participant && `${participant.lastName || ''} ${participant.firstName || ''}`}
                          {participant && participant.weight && (
                            <Tag color="default" style={{ marginLeft: '6px', fontSize: '11px', borderRadius: '10px' }}>
                              {participant.weight} кг
                            </Tag>
                          )}
                        </span>
                        {(participant && participant.clubName || participant && participant.club?.name) && (
                          <Tag color="blue" style={{ fontSize: '11px', borderRadius: '10px', marginLeft: '4px' }}>
                            {participant.clubName || participant.club?.name}
                          </Tag>
                        )}
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isUnregistering}
                          onClick={() => handleUnregisterParticipant(participantId, category.id)}
                          style={{ 
                            marginLeft: '6px', 
                            padding: '0 4px', 
                            height: '20px', 
                            lineHeight: '20px',
                            fontSize: '12px'
                          }}
                          title="Снять с регистрации"
                        />
                      </RegisteredParticipantBadge>
                    );
                  } else {
                    // Если участник не найден в списке
                    const isUnregistering = unregisteringParticipants.has(`${participantId}-${category.id}`);
                    return (
                      <Tag key={`unknown-${participantId}`} color="warning" style={{ borderRadius: '16px', padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        Участник ID: {participantId}
                        <Button
                          type="text"
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          loading={isUnregistering}
                          onClick={() => handleUnregisterParticipant(participantId, category.id)}
                          style={{ 
                            padding: '0 2px', 
                            height: '16px', 
                            lineHeight: '16px',
                            fontSize: '10px'
                          }}
                          title="Снять с регистрации"
                        />
                      </Tag>
                    );
                  }
                })}
                </div>
              );
            })()}
          </div>



          {/* Отображаем список выбранных участников */}
          {selectedParticipants[category.id] && selectedParticipants[category.id].length > 0 && (
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <Text strong style={{ fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
                <UserOutlined style={{ color: '#1890ff' }} />
                Выбранные участники:
              </Text>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {selectedParticipants[category.id].map(participantId => {
                  const participant = participants.find(p => p.id === participantId);
                  if (!participant) return null;

                  return (
                    <SelectedParticipantChip key={participant.id}>
                      <span>{`${participant.lastName} ${participant.firstName}`}</span>
                      {participant.weight && (
                        <Tag color="default" style={{ margin: 0, fontSize: '11px', borderRadius: '10px' }}>
                          {participant.weight} кг
                        </Tag>
                      )}
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        style={{ 
                          marginLeft: '4px', 
                          padding: '0 4px', 
                          height: '20px', 
                          lineHeight: '20px',
                          fontSize: '12px'
                        }}
                        onClick={() => {
                          const newSelected = [...(selectedParticipants[category.id] || [])];
                          const index = newSelected.indexOf(participantId);
                          if (index !== -1) {
                            newSelected.splice(index, 1);
                            handleParticipantSelect(category.id, newSelected);
                          }
                        }}
                      />
                    </SelectedParticipantChip>
                  );
                })}
              </div>
            </div>
          )}

          {eligibleParticipants.length > 0 ? (
            <Select
              style={{ width: '100%' }}
              placeholder="Выберите участников"
              mode="multiple"
              value={selectedParticipants[category.id] || []}
              onChange={(values: number[]) => handleParticipantSelect(category.id, values)}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) => {
                if (!option || !option.children) return false;
                const childText = String(option.children);
                return childText.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }}
              maxTagCount={3}
              maxTagPlaceholder={(omittedValues) => `+ ${omittedValues.length} участников`}
              dropdownRender={menu => (
                <div>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <div style={{ padding: '0 8px 8px', background: '#fafafa', borderRadius: '0 0 4px 4px' }}>
                    <Space>
                      <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={Boolean(participantCount.hasLimit && participantCount.availableSlots != null && participantCount.availableSlots <= 0)}
                        onClick={() => {
                          // Select all eligible participants that are not already registered, но не больше лимита мест
                          const registeredIds = new Set<number>();
                          categoryParticipantsMap[category.id]?.forEach(p => {
                            if (p && p.participantId) registeredIds.add(Number(p.participantId));
                            if (p && p.userId) registeredIds.add(Number(p.userId));
                          });

                          let newSelectedIds = sortedParticipants
                            .filter(p => p.id && !registeredIds.has(Number(p.id)))
                            .map(p => p.id);
                          if (participantCount.hasLimit && participantCount.availableSlots != null && participantCount.availableSlots >= 0 && newSelectedIds.length > participantCount.availableSlots) {
                            newSelectedIds = newSelectedIds.slice(0, participantCount.availableSlots);
                          }
                          handleParticipantSelect(category.id, newSelectedIds);
                        }}
                        style={{ borderRadius: '6px' }}
                      >
                        Выбрать всех
                      </Button>
                      <Button
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleParticipantSelect(category.id, [])}
                        style={{ borderRadius: '6px' }}
                      >
                        Очистить
                      </Button>
                    </Space>
                  </div>
                </div>
              )}
            >
              {sortedParticipants.map(participant => {

                return (
                  <Option
                    key={participant.id}
                    value={Number(participant.id)}
                    disabled={
                      // Проверяем, выбран ли участник в других категориях
                      Object.entries(selectedParticipants)
                        .filter(([catId]) => Number(catId) !== category.id) // Исключаем текущую категорию
                        .flatMap(([_, ids]) => ids) // Получаем все ID участников из других категорий
                        .includes(Number(participant.id)) ||
                      // Проверяем, зарегистрирован ли участник уже в этой категории
                      (registeredParticipants[String(participant.id)] === category.id) ||
                      // Не даём добавить сверх лимита: если мест нет или уже выбрано максимум — новые варианты отключены
                      (participantCount.hasLimit && participantCount.availableSlots != null && participantCount.availableSlots <= 0) ||
                      (participantCount.hasLimit && participantCount.availableSlots != null && (selectedParticipants[category.id]?.length || 0) >= participantCount.availableSlots && !selectedParticipants[category.id]?.includes(Number(participant.id)))
                    }
                  >
                    {registeredParticipants[String(participant.id)] === category.id ? (
                      <span>
                        {`${participant.lastName} ${participant.firstName}${participant.middleName ? ` ${participant.middleName}` : ''}`}
                        <span style={{ color: 'green', marginLeft: '5px' }}>
                          (уже зарегистрирован)
                        </span>
                      </span>
                    ) : (
                      `${participant.lastName} ${participant.firstName}${participant.middleName ? ` ${participant.middleName}` : ''}`
                    )}
                  </Option>
                );
              })}
            </Select>
          ) : (
            <Alert
              message="Нет подходящих участников для этой категории"
              type="warning"
              showIcon
              style={{ marginTop: '10px' }}
            />
          )}
        </Space>
      )
    };
    } catch (categoryError) {
      logger.error(`Error rendering category ${category.id}:`, categoryError);
      return null;
    }
  }, [participants, userClub, tournamentCategories, registeredParticipants, selectedParticipants, getCategoryParticipantCount, handleParticipantSelect, categoryParticipantsMap, getRegisteredParticipantsForCategory, unregisteringParticipants, handleUnregisterParticipant]);

  // Дополнительная проверка перед рендерингом
  try {
    return (
      <StyledModal
        title={
          <span>
            <TeamOutlined style={{ marginRight: '8px' }} />
            Регистрация участников
          </span>
        }
        open={visible}
        onCancel={onCancel}
        width={900}
        style={{ top: 20 }}
        footer={[
          <Button
            key="unregister"
            danger
            icon={<DeleteOutlined />}
            onClick={handleUnregisterClub}
            loading={loading}
            size="large"
            style={{
              borderRadius: '6px',
              height: '40px',
              marginRight: 'auto'
            }}
          >
            Сняться всей командой
          </Button>,
          <Button
            key="cancel"
            onClick={handleCancel}
            size="large"
            style={{
              borderRadius: '6px',
              height: '40px'
            }}
          >
            Отмена
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            loading={loading}
            disabled={Object.values(selectedParticipants).flat().length === 0}
            size="large"
            style={{
              borderRadius: '6px',
              height: '40px',
              fontWeight: 500
            }}
          >
            {Object.values(selectedParticipants).flat().length > 0
              ? `Зарегистрировать (${Object.values(selectedParticipants).flat().length} участников)`
              : "Зарегистрировать"}
          </Button>
        ]}
      >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Загрузка...
        </div>
      ) : (
        <>
          {loadingCategories ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spin>
                <div style={{ padding: '30px', textAlign: 'center' }}>
                  Загрузка категорий...
                </div>
              </Spin>
            </div>
          ) : (
            <>
              <Alert
                message="Важная информация"
                description={
                  <span>
                    В каждой категории отображаются только спортсмены, у которых в клубе указана эта категория.
                    Если спортсмен не отображается, задайте ему категорию в{' '}
                    <Link to="/club" style={{ fontWeight: 500 }}>
                      управлении клубом
                    </Link>.
                  </span>
                }
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              {participants.length > 0 ? (
                <Alert
                  message={
                    tournamentCategories.length > 0
                      ? (() => {
                          const tournamentCategoryIds = new Set(
                            tournamentCategories.map(c => Number(c.categoryId ?? c.id)).filter(Boolean)
                          );
                          const matchingCount = participants.filter(
                            p => (p.categoryId ?? (p as any).category?.id) != null &&
                              tournamentCategoryIds.has(Number(p.categoryId ?? (p as any).category?.id))
                          ).length;
                          return matchingCount < participants.length
                            ? `Загружено ${participants.length} участников клуба. Подходят под категории турнира: ${matchingCount}`
                            : `Загружено ${participants.length} участников клуба`;
                        })()
                      : `Загружено ${participants.length} участников клуба`
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              ) : null}

              <StyledCollapse
                ghost
                size="small"
                activeKey={collapsedCategories}
                onChange={(keys) => setCollapsedCategories(keys as string[])}
                items={availableCategories.map(renderCategoryCard).filter((item) => item !== null) as Array<{ key: string; label: React.ReactNode; style?: React.CSSProperties; children: React.ReactNode }>}
              />

              {/* Информация о скрытых категориях */}
              {(() => {
                const hiddenCount = tournamentCategories.length - availableCategories.length;

                if (hiddenCount > 0) {
                  return (
                    <Alert
                      message={`Скрыто ${hiddenCount} ${hiddenCount === 1 ? 'категория' : hiddenCount < 5 ? 'категории' : 'категорий'} без доступных участников`}
                      description="Показываются только категории, в которые можно зарегистрировать участников"
                      type="info"
                      showIcon
                      style={{ marginTop: '16px' }}
                    />
                  );
                }
                return null;
              })()}

              {availableCategories.length === 0 && tournamentCategories.length > 0 && (
                <Alert
                  message="Нет доступных категорий для регистрации"
                  description="Все участники уже зарегистрированы или нет подходящих участников для существующих категорий"
                  type="warning"
                  showIcon
                  style={{ marginTop: '16px' }}
                />
              )}

              {tournamentCategories.length === 0 && (
                <Alert
                  message="Для данного турнира еще не созданы категории"
                  type="warning"
                />
              )}
            </>
          )}
        </>
      )}
    </StyledModal>
    );
  } catch (renderError) {
    logger.error('Error rendering RegisterParticipantsModal:', renderError);
    return (
      <StyledModal
        title={
          <span>
            <CloseCircleOutlined style={{ marginRight: '8px', color: '#ff4d4f' }} />
            Ошибка
          </span>
        }
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel} size="large" style={{ borderRadius: '6px' }}>
            Закрыть
          </Button>
        ]}
      >
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Alert
            message="Ошибка загрузки"
            description="Произошла ошибка при загрузке модала регистрации участников. Пожалуйста, попробуйте еще раз или обратитесь к администратору."
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        </div>
      </StyledModal>
    );
  }
};
